<script setup>
import { computed, ref, onMounted, onUnmounted } from "vue"
import { store } from '@/store/index.js'
import { useMsal } from '@/composition-api/useMsal.js'
import { loginRequest } from "@/adAuth-config"
import { getAuthProvider } from '@/oidc-config.js'
import { azureADAuthentication } from '@/components/ui/AzureADAuthentication.js'

const { instance } = useMsal()

const oidcIsAuthenticated = computed(() => store.getters['oidc/oidcIsAuthenticated'])
const adIsAuthenticated = () => store.state.bearerToken !== null

const getProvider = () => getAuthProvider(store.state.oidc?.user)

// Static role mapping
const roleDescriptions = {
  RnDemployee: "R&D Employee",
  RnDconsultant: "R&D Consultant",
  RnDAdvancedAnalysisPlatform: "Product Analytics Internal",
  ODP_unlimited: "Basic for Scania R&D",
  ODP_limited: "Basic for Scania",
  Product_Analytics_Download: "Download",
  Freud_standard: "Freud Standard",
  Product_Quality: "Basic for Scania"
}

// Priority list for sorting roles
const priorityItems = [
  "RnDemployee",
  "RnDconsultant",
  "RnDAdvancedAnalysisPlatform",
  "ODP_unlimited",
  "ODP_limited",
  "YQI_Users"
]

// Precompute user roles based on localStorage
const userRoleDescription = computed(() => {
  const roles = localStorage.getItem('yourAdRoles')?.split(",") || []
  return sortByPriority(roles, priorityItems).map(role => roleDescriptions[role] || role)
})

// Function to sort the array based on priority
const sortByPriority = (array, priorityList) => {
  if (!Array.isArray(array)) return [] // Ensure array is valid

  return array.sort((x, y) => {
    const xPriority = priorityList?.includes(x) ? priorityList.indexOf(x) : priorityList.length
    const yPriority = priorityList?.includes(y) ? priorityList.indexOf(y) : priorityList.length
    return xPriority - yPriority // Sort priority items first
  })
}

// Authentication logic
const authenticate = async () => {
  console.log('Authenticating...')
  try {
    if (!oidcIsAuthenticated.value) {
      await store.dispatch('oidc/authenticateOidc', true)
    }

    const provider = getProvider()

    if (provider === 'SCANIA') {
      const accounts = instance.getAllAccounts()

      if (!accounts.length) {
        localStorage.setItem('redirectUrl', window.location.pathname)
        await instance.loginRedirect(loginRequest)
        return
      }

      await azureADAuthentication()
      return
    }

    console.log('Provider is not SCANIA. Azure auth skipped.', provider)
  } catch (error) {
    console.error('Authentication error:', error)
    throw error
  }
}

// Logout logic
const handleLogout = async () => {
  console.log("Logging out...")
  isOpen.value = false // Close dropdown
  try {
    await store.dispatch('oidc/signOutOidcSilent', true)

    const provider = getProvider()
    if (provider === 'SCANIA') {
      instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin + "/"
      })
    }

    localStorage.setItem('adTokenUpdate', null)
    localStorage.setItem('sasTokenUpdate', null)
    console.log('Signed out')
  } catch (error) {
    console.error('Logout failed:', error)
  }
}

// Reactive variables
const isOpen = ref(false)
const isMenuOpen = ref(false)

const closeMenu = () => {
  isMenuOpen.value = false
}

// Close dropdown when clicking outside
const dropdown = ref(null)
const closeDropdown = (event) => {
  if (dropdown.value && !dropdown.value.contains(event.target)) {
    isOpen.value = false
  }
}

// Add event listener on mount
onMounted(() => {
  document.addEventListener("click", closeDropdown)
})

// Remove event listener when component unmounts
onUnmounted(() => {
  document.removeEventListener("click", closeDropdown)
})
</script>

<template>
  <div class="navigation">
    <nav :class="{ 'active': isMenuOpen }">
      <!-- Navigation Links -->
      <ul class="menu-list">
        <!-- Home with Dropdown -->
        <li class="menu-item">
          <button v-if="!adIsAuthenticated() && !oidcIsAuthenticated" class="btn btn btn-secondary" @click="authenticate()">
            Login &nbsp;
          </button>
          <button v-if="adIsAuthenticated() || oidcIsAuthenticated" class="btn btn btn-secondary" @click="closeMenu()">
            <font-awesome-icon icon="fa-user" /> &nbsp;
            {{ store.state.username?.toUpperCase().replace('@SCANIA.COM', '').replace('.', ' ') || 'USER' }}
          </button>
          <ul v-if="oidcIsAuthenticated || adIsAuthenticated()" class="dropdown">
            <li class="dropdown-header">Your Roles:</li>
            <li class="dropdown-item">Provider: {{ getProvider() || 'UNKNOWN' }}</li>
            <li v-for="(item, index) in userRoleDescription" :key="index" class="dropdown-item">
              {{ item }}
            </li>
            <hr class="dropdown-divider" />
            <li class="dropdown-footer">
              <button class="btn btn-sm btn-primary" @click="handleLogout">Logout</button>
            </li>
          </ul>
        </li>
      </ul>
    </nav>
  </div>
</template>


<style scoped>
/* Styling for navigation */
.navigation {
  display: flex;
  align-items: center;
  position: relative;
  padding: 12px;
  z-index: 999; /* Ensure the navigation stays on top of other content */
}

nav ul.menu-list {
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0;
  justify-content: flex-start;
}

.menu-list .menu-item {
  position: relative; /* For dropdown positioning */
  width: fit-content;
}

.menu-list .menu-item .dropdown {
  display: none;
  position: absolute;
  top: 100%;
  background-color: white;
  border: 1px solid #ccc;
  list-style: none;
  padding: 5px;
  border-radius: 2%;
  width: fit-content;
  margin-left: -20px;
}

.menu-list .menu-item:hover .dropdown {
  display: block;
}

.menu-list .dropdown li {
  padding: 5px;
}

.menu-list .dropdown li a {
  color: #333;
  text-decoration: none;
}

.menu-list .parent {
  color: #333;
  text-decoration: none;
  margin-right: 25px;
}

hr {
  width: 100%;
  border: 1px solid #ccc;
  margin: 10px 0;
}

.menu-links {
  display: flex;
  align-items: center;
  margin-left: auto; /* Push content to the right */
  gap: 15px;
}

.vertical-line {
  border-left: 1px solid #ccc;
  height: 30px;
  margin-right: 10px;
}

/* Optional: Mobile Responsiveness */
@media (max-width: 768px) {
  nav ul.menu-list {
    flex-direction: column;
  }
}

/* Dropdown Container */
.dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: 200px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  padding: 10px 0;
  list-style: none;
  z-index: 1000;
  border: 1px solid #ddd;
  animation: fadeIn 0.2s ease-in-out;
}

/* Dropdown Header */
.dropdown-header {
  font-weight: bold;
  padding: 10px 15px;
  border-bottom: 1px solid #ddd;
}

.dropdown-item:hover {
  background: #f0f0f0;
}

/* Dropdown Divider */
.dropdown-divider {
  margin: 5px 0;
  border: none;
  border-top: 1px solid #ddd;
}

/* Dropdown Footer */
.dropdown-footer {
  padding: 10px;
  text-align: center;
}

/* Fade-in Animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
